
import { supabase } from '@/lib/supabase';

export type SubscriptionEventType = 
  | 'plan_started' 
  | 'days_remaining_update'
  | 'plan_expired'
  | 'grace_period_started'
  | 'grace_period_ended'
  | 'upgrade_viewed'
  | 'upgrade_clicked'
  | 'downgrade_attempted'
  | 'plan_renewed'
  | 'referral_stored'
  | 'referral_click'
  | 'referral_attributed';

interface SubscriptionEventData {
  plan_type?: string;
  days_remaining?: number;
  previous_plan?: string;
  new_plan?: string;
  source_page?: string;
  metadata?: Record<string, any>;
  requires_auth?: boolean;
  referral_code?: string; // Add referral_code as a valid property
}

/**
 * Track subscription-related events for analytics
 */
export const trackSubscriptionEvent = async (
  eventType: SubscriptionEventType,
  eventData: SubscriptionEventData = {}
): Promise<boolean> => {
  try {
    // Log event to console for debugging
    console.log(`[SUBSCRIPTION ANALYTICS] ${eventType}`, eventData);
    
    // Attempt to save event to the database
    const { error } = await supabase.from('subscription_events').insert({
      event_type: eventType,
      event_data: eventData,
    });
    
    if (error) {
      console.error('Error tracking subscription event:', error);
      return false;
    }
    
    return true;
  } catch (err) {
    console.error('Exception in trackSubscriptionEvent:', err);
    return false;
  }
};

/**
 * Track when a user views subscription plans with intent to upgrade
 */
export const trackUpgradeView = async (
  sourcePage: string, 
  currentPlan?: string,
  metadata: Record<string, any> = {}
): Promise<void> => {
  await trackSubscriptionEvent('upgrade_viewed', {
    source_page: sourcePage,
    previous_plan: currentPlan,
    metadata
  });
};

/**
 * Track when a user's plan status changes to expired
 */
export const trackPlanExpired = async (
  planType: string,
  metadata: Record<string, any> = {}
): Promise<void> => {
  await trackSubscriptionEvent('plan_expired', {
    plan_type: planType,
    metadata
  });
};

/**
 * Track when a grace period starts for an expired plan
 */
export const trackGracePeriodStarted = async (
  planType: string,
  daysRemaining: number,
  metadata: Record<string, any> = {}
): Promise<void> => {
  await trackSubscriptionEvent('grace_period_started', {
    plan_type: planType,
    days_remaining: daysRemaining,
    metadata
  });
};

/**
 * Track when a user attempts to downgrade their plan
 */
export const trackDowngradeAttempt = async (
  currentPlan: string,
  attemptedPlan: string,
  metadata: Record<string, any> = {}
): Promise<void> => {
  await trackSubscriptionEvent('downgrade_attempted', {
    previous_plan: currentPlan,
    new_plan: attemptedPlan,
    metadata
  });
};
